import { useMutation } from '@tanstack/react-query'
import {
  App,
  Button,
  Divider,
  Flex,
  Form,
  Input,
  Tooltip,
  Typography
} from 'antd'
import { createStyles } from 'antd-style'
import { Mail } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { apiErrorCode, LoginMethod, track } from 'src/analytics'
import { usersLoginAdminCreateMutation } from 'src/api/main'
import { LOGIN_IN_PROGRESS_KEY } from 'src/constants'
import { emailOtpRequest, emailOtpVerify } from 'src/features/auth/api/emailOtp'
import { useResendCountdown } from 'src/features/auth/hooks'
import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import { setLocalStorageToken, useInlineApiErrors } from 'src/utils'

import { AuthLink } from '../components'
import { schedulePasskeyPrompt } from '../components/PasskeyRecommendationModal'
import { asAuthenticatedTokens } from '../utils/authResponseAdapters'

const { OTP } = Input
const { Text, Title } = Typography

const useStyles = createStyles(({ token }) => ({
  otp: {
    'input.ant-otp-input': {
      width: '36px !important',
      height: '36px !important'
    }
  },
  envelope: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: token.colorPrimaryBg,
    color: token.colorPrimaryText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  maskedEmailTrigger: {
    cursor: 'help',
    textDecoration: 'underline dotted',
    textUnderlineOffset: 2
  }
}))

type EmailOtpLocationState = {
  __otpAccessToken?: string | null
  __created_at?: string
  expires_in?: number
  wait?: number
  masked_destination?: string
  submitted_email?: string
}

export const EmailOtpVerify = () => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { message } = App.useApp()
  const { showApiErrors, errorAlert } = useInlineApiErrors()
  const { updateUserData } = useSessionContext()

  const locationState = useMemo(
    () => (location.state as EmailOtpLocationState | null) ?? null,
    [location.state]
  )
  const [showCodeForm, setShowCodeForm] = useState(false)

  /**
   * Codes submitted on this screen. A ref, not state: nothing renders it, and the
   * OTP input auto-submits on the sixth character, so a re-render per attempt
   * would fight the field. Counted because "how many tries did it take" is the
   * only way to tell a hard-to-read code from a hard-to-find email.
   */
  const attempts = useRef(0)
  const expirationTime = useResendCountdown({
    createdAt: locationState?.__created_at,
    expiresInSeconds: locationState?.expires_in,
    waitSeconds: locationState?.wait
  })

  useEffect(() => {
    if (!locationState) {
      navigate(paths.auth.login, { replace: true })
    }
  }, [locationState, navigate])

  useEffect(() => {
    if (expirationTime.expired) {
      form.setFields([
        {
          name: 'otp_code',
          errors: [t('auth.emailOtp.codeExpired')],
          value: ''
        }
      ])
    } else {
      form.resetFields(['otp_code'])
    }
  }, [expirationTime.expired, form, t])

  const { mutateAsync: fetchUserAfterLogin, isPending: isPendingUser } =
    useMutation({
      ...usersLoginAdminCreateMutation(),
      onSuccess: (response) => {
        sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
        updateUserData(response)
        message.success(t('login_success'))
        navigate(paths.home)
      },
      onError: (e) => {
        sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
        showApiErrors(e)
      }
    })

  const { mutate: verifyOtp, isPending: isVerifyPending } = useMutation({
    mutationFn: async (values: { otp_code: string }) => {
      attempts.current += 1

      if (!locationState?.__otpAccessToken) {
        throw new Error('Missing OTP access token')
      }
      const { data } = await emailOtpVerify(
        { otp_code: values.otp_code },
        locationState.__otpAccessToken
      )

      return data
    },
    onSuccess: async (response) => {
      const tokens = asAuthenticatedTokens(response)
      if (tokens) {
        track('login_completed', {
          method: LoginMethod.EMAIL_OTP,
          attempts: attempts.current
        })
        schedulePasskeyPrompt()
        setLocalStorageToken(tokens)
        await fetchUserAfterLogin({})
        return
      }
      showApiErrors(new Error('Missing tokens in verify response'))
    },
    onError: (error) => {
      const axiosError = error as {
        response?: { data?: { code?: string; detail?: string } }
      }
      const code = axiosError.response?.data?.code

      // The server's own `code` when it sent one — it is a fixed vocabulary
      // (`token_not_valid`, `otp_*`), unlike `detail`, which is localised prose
      // and can quote the user back. Falls back to the status code.
      track('login_failed', {
        method: LoginMethod.EMAIL_OTP,
        error_code: code ?? apiErrorCode(error),
        attempts: attempts.current
      })

      if (code === 'token_not_valid') {
        message.error(t('auth.emailOtp.tokenExpired'))
        navigate(paths.auth.login, { replace: true })
        return
      }

      if (code?.includes('otp')) {
        const detail = axiosError.response?.data?.detail
        if (detail) {
          form.setFields([{ name: 'otp_code', errors: [detail] }])
          return
        }
      }

      showApiErrors(error)
    }
  })

  const { mutate: resendOtp, isPending: isResendPending } = useMutation({
    mutationFn: async () => {
      if (!locationState?.submitted_email) {
        throw new Error('Missing email to resend to')
      }
      const serviceToken = import.meta.env.VITE_APP_SERVICE_TOKEN as
        | string
        | undefined
      const { data } = await emailOtpRequest({
        username: locationState.submitted_email,
        ...(serviceToken ? { service_token: serviceToken } : {})
      })

      return data
    },
    onSuccess: (data) => {
      track('login_code_requested', {
        method: LoginMethod.EMAIL_OTP,
        resend: true
      })
      message.success(t('auth.emailOtp.codeSent'))
      navigate(
        { pathname: location.pathname },
        {
          replace: true,
          state: {
            ...locationState,
            __otpAccessToken: data.otp_access_token,
            __created_at: new Date().toISOString(),
            expires_in: data.expires_in,
            wait: data.wait,
            masked_destination: data.masked_destination
          }
        }
      )
    },
    onError: (error) => showApiErrors(error)
  })

  const onSubmit = async () => {
    try {
      const values = await form.validateFields()
      verifyOtp(values)
    } catch {
      // form errors already shown inline
    }
  }

  const maskedEmail = locationState?.masked_destination ?? ''
  const submittedEmail = locationState?.submitted_email ?? ''
  const displayEmail = maskedEmail || submittedEmail
  const showMaskedEmailTooltip = Boolean(
    submittedEmail && maskedEmail && submittedEmail !== maskedEmail
  )
  const resendTooltipTitle = expirationTime.canResend
    ? undefined
    : t('auth.resendInSeconds', { count: expirationTime.resendInSeconds })

  const renderEmailSubtitle = (
    i18nKey: 'auth.emailOtp.linkSentSubtitle' | 'auth.emailOtp.codeSubtitle'
  ) => (
    <Text type="secondary">
      <Trans
        i18nKey={i18nKey}
        values={{ email: displayEmail }}
        components={{
          email: showMaskedEmailTooltip ? (
            <Tooltip title={submittedEmail}>
              <span className={styles.maskedEmailTrigger} />
            </Tooltip>
          ) : (
            <span />
          )
        }}
      />
    </Text>
  )

  if (!showCodeForm) {
    return (
      <Flex vertical gap={32} style={{ width: '100%' }}>
        {errorAlert}

        <Flex vertical gap={20} align="center" style={{ textAlign: 'center' }}>
          <div className={styles.envelope}>
            <Mail size={28} strokeWidth={1.75} />
          </div>
          <Flex vertical gap={8}>
            <Title level={2} style={{ marginBottom: 0 }}>
              {t('auth.emailOtp.linkSentTitle')}
            </Title>
            {renderEmailSubtitle('auth.emailOtp.linkSentSubtitle')}
          </Flex>
        </Flex>

        <Flex vertical gap={12}>
          <Flex justify="center">
            <Tooltip title={resendTooltipTitle}>
              <span style={{ display: 'inline-flex' }}>
                <Button
                  type="link"
                  disabled={!expirationTime.canResend}
                  loading={isResendPending}
                  onClick={() => resendOtp()}
                >
                  {t('auth.emailOtp.linkResend')}
                </Button>
              </span>
            </Tooltip>
          </Flex>

          <Divider plain style={{ margin: '8px 0' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('auth.emailOtp.or')}
            </Text>
          </Divider>

          <Button
            block
            onClick={() => setShowCodeForm(true)}
            data-testid="email-otp-enter-code-toggle"
          >
            {t('auth.emailOtp.enterCodeInstead')}
          </Button>

          <Flex justify="center">
            <AuthLink to={paths.auth.login} replace>
              {t('auth.emailOtp.backToSignIn')}
            </AuthLink>
          </Flex>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={24} style={{ width: '100%' }}>
      <Flex vertical gap={8}>
        <Title level={2} style={{ marginBottom: 0 }}>
          {t('auth.emailOtp.codeTitle')}
        </Title>
        {renderEmailSubtitle('auth.emailOtp.codeSubtitle')}
      </Flex>

      {errorAlert}

      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="otp_code"
          label={t('auth.emailOtp.codeLabel')}
          rules={[{ required: true, message: t('auth.emailOtp.codeRequired') }]}
        >
          <OTP
            autoFocus
            disabled={expirationTime.expired}
            formatter={(str) => str.replace(/\D/g, '')}
            className={styles.otp}
            onChange={(value) => {
              // Auto sign-in once the full 6-digit code is entered/pasted, so
              // the user doesn't need to press the submit button.
              if (
                value.length === 6 &&
                !expirationTime.expired &&
                !isVerifyPending
              ) {
                verifyOtp({ otp_code: value })
              }
            }}
          />
        </Form.Item>

        <Flex align="center" gap={4} style={{ marginBottom: 16 }}>
          {!expirationTime.expired && (
            <>
              <Text type="secondary">
                {t('auth.emailOtp.codeExpiresIn', {
                  time: expirationTime.formattedTime
                })}
              </Text>
              <Text type="secondary" aria-hidden>
                ·
              </Text>
            </>
          )}
          <Tooltip title={resendTooltipTitle}>
            <span style={{ display: 'inline-flex' }}>
              <Button
                type="link"
                disabled={!expirationTime.canResend}
                loading={isResendPending}
                onClick={() => resendOtp()}
                style={{ paddingInline: 0 }}
              >
                {t('auth.emailOtp.resend')}
              </Button>
            </span>
          </Tooltip>
        </Flex>

        <Flex gap={16} vertical>
          <Button
            block
            size="large"
            type="primary"
            loading={isVerifyPending || isPendingUser}
            onClick={onSubmit}
          >
            {t('sign_in')}
          </Button>
          <Flex justify="center" gap={16}>
            <AuthLink onClick={() => setShowCodeForm(false)}>
              {t('auth.emailOtp.useLinkInstead')}
            </AuthLink>
            <AuthLink to={paths.auth.login} replace>
              {t('auth.emailOtp.backToSignIn')}
            </AuthLink>
          </Flex>
        </Flex>
      </Form>
    </Flex>
  )
}
